/**
 * Spaces Service
 * Service for DigitalOcean Spaces operations
 */

import api from "../api";

class SpacesService {
  /**
   * List all user's Spaces
   * @returns {Promise<Array>}
   */
  async listSpaces() {
    try {
      const response = await api.get("/spaces");
      return response.data || [];
    } catch (error) {
      console.error("Error listing spaces:", error);
      throw error;
    }
  }

  /**
   * Get single Space details
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<object>}
   */
  async getSpace(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching space:", error);
      throw error;
    }
  }

  /**
   * Create order for a new Space
   * @param {object} config - Space configuration
   * @returns {Promise<object>}
   */
  async createOrder(config) {
    try {
      const response = await api.post("/spaces/order", config);
      return response.data;
    } catch (error) {
      console.error("Error creating space order:", error);
      throw error;
    }
  }

  /**
   * Request deletion token for a Space
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<object>}
   */
  async requestDeletionToken(spaceId) {
    try {
      const response = await api.post(`/spaces/${spaceId}/request-deletion-token`);
      return response.data;
    } catch (error) {
      console.error("Error requesting deletion token:", error);
      throw error;
    }
  }

  /**
   * Verify deletion token
   * @param {number|string} spaceId - Space ID
   * @param {string} token - Deletion token
   * @returns {Promise<object>}
   */
  async verifyDeletionToken(spaceId, token) {
    try {
      const response = await api.post(`/spaces/${spaceId}/verify-deletion-token`, {
        token,
      });
      return response.data;
    } catch (error) {
      console.error("Error verifying deletion token:", error);
      throw error;
    }
  }

  /**
   * Delete a Space (requires token)
   * @param {number|string} spaceId - Space ID
   * @param {string} token - Deletion token
   * @returns {Promise<void>}
   */
  async deleteSpace(spaceId, token) {
    try {
      await api.delete(`/spaces/${spaceId}`, {
        data: { token },
      });
    } catch (error) {
      console.error("Error deleting space:", error);
      throw error;
    }
  }

  // CORS Management

  /**
   * Update CORS configuration
   * @param {number|string} spaceId - Space ID
   * @param {Array} corsRules - CORS rules array
   * @returns {Promise<object>}
   */
  async updateCors(spaceId, corsRules) {
    try {
      const response = await api.put(`/spaces/${spaceId}/cors`, { corsRules });
      return response.data;
    } catch (error) {
      console.error("Error updating CORS:", error);
      throw error;
    }
  }

  /**
   * Get CORS configuration
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<Array>}
   */
  async getCors(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}/cors`);
      return response.data || [];
    } catch (error) {
      console.error("Error getting CORS:", error);
      throw error;
    }
  }

  /**
   * Delete CORS configuration
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<void>}
   */
  async deleteCors(spaceId) {
    try {
      await api.delete(`/spaces/${spaceId}/cors`);
    } catch (error) {
      console.error("Error deleting CORS:", error);
      throw error;
    }
  }

  // ACL Management

  /**
   * Update ACL (Access Control List)
   * @param {number|string} spaceId - Space ID
   * @param {string} acl - ACL value (private, public-read, public-read-write)
   * @returns {Promise<object>}
   */
  async updateAcl(spaceId, acl) {
    try {
      const response = await api.put(`/spaces/${spaceId}/acl`, { acl });
      return response.data;
    } catch (error) {
      console.error("Error updating ACL:", error);
      throw error;
    }
  }

  /**
   * Get ACL configuration
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<object>}
   */
  async getAcl(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}/acl`);
      return response.data;
    } catch (error) {
      console.error("Error getting ACL:", error);
      throw error;
    }
  }

  // File Operations

  /**
   * List files in a Space
   * @param {number|string} spaceId - Space ID
   * @param {object} options - List options (prefix, maxKeys, continuationToken)
   * @returns {Promise<object>}
   */
  async listFiles(spaceId, options = {}) {
    try {
      const { prefix = "", maxKeys = 1000, continuationToken } = options;
      const response = await api.get(`/spaces/${spaceId}/files`, {
        params: { prefix, maxKeys, continuationToken },
      });
      return response.data;
    } catch (error) {
      console.error("Error listing files:", error);
      throw error;
    }
  }

  /**
   * Upload file to a Space
   * @param {number|string} spaceId - Space ID
   * @param {File} file - File object
   * @param {string} key - Object key (file path)
   * @param {object} metadata - Optional metadata
   * @returns {Promise<object>}
   */
  async uploadFile(spaceId, file, key, metadata = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("key", key);
      if (metadata.contentType) {
        formData.append("contentType", metadata.contentType);
      }

      const response = await api.post(`/spaces/${spaceId}/files`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  }

  /**
   * Delete file from a Space
   * @param {number|string} spaceId - Space ID
   * @param {string} key - Object key
   * @returns {Promise<void>}
   */
  async deleteFile(spaceId, key) {
    try {
      await api.delete(`/spaces/${spaceId}/files/${encodeURIComponent(key)}`);
    } catch (error) {
      console.error("Error deleting file:", error);
      throw error;
    }
  }

  /**
   * Get presigned download URL for a file
   * @param {number|string} spaceId - Space ID
   * @param {string} key - Object key
   * @param {number} expiresIn - URL expiration in seconds (default 3600)
   * @returns {Promise<string>}
   */
  async getDownloadUrl(spaceId, key, expiresIn = 3600) {
    try {
      const response = await api.get(
        `/spaces/${spaceId}/files/${encodeURIComponent(key)}/url`,
        {
          params: { expiresIn },
        }
      );
      return response.data.url;
    } catch (error) {
      console.error("Error getting download URL:", error);
      throw error;
    }
  }

  // Lifecycle Management

  /**
   * Update lifecycle configuration
   * @param {number|string} spaceId - Space ID
   * @param {Array} rules - Lifecycle rules array
   * @returns {Promise<object>}
   */
  async updateLifecycle(spaceId, rules) {
    try {
      const response = await api.put(`/spaces/${spaceId}/lifecycle`, { rules });
      return response.data;
    } catch (error) {
      console.error("Error updating lifecycle:", error);
      throw error;
    }
  }

  /**
   * Get lifecycle configuration
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<Array>}
   */
  async getLifecycle(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}/lifecycle`);
      return response.data || [];
    } catch (error) {
      console.error("Error getting lifecycle:", error);
      throw error;
    }
  }

  /**
   * Delete lifecycle configuration
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<void>}
   */
  async deleteLifecycle(spaceId) {
    try {
      await api.delete(`/spaces/${spaceId}/lifecycle`);
    } catch (error) {
      console.error("Error deleting lifecycle:", error);
      throw error;
    }
  }

  // Versioning Management

  /**
   * Update versioning status
   * @param {number|string} spaceId - Space ID
   * @param {string} status - Versioning status (Enabled, Suspended)
   * @returns {Promise<object>}
   */
  async updateVersioning(spaceId, status) {
    try {
      const response = await api.put(`/spaces/${spaceId}/versioning`, {
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating versioning:", error);
      throw error;
    }
  }

  /**
   * Get versioning status
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<object>}
   */
  async getVersioning(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}/versioning`);
      return response.data;
    } catch (error) {
      console.error("Error getting versioning:", error);
      throw error;
    }
  }

  /**
   * Update ACL for a specific object
   * @param {number|string} spaceId - Space ID
   * @param {string} key - Object key
   * @param {string} acl - ACL value (private, public-read)
   * @returns {Promise<object>}
   */
  async updateObjectAcl(spaceId, key, acl) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await api.put(
        `/spaces/${spaceId}/files/${encodedKey}/acl`,
        {
          acl,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating object ACL:", error);
      throw error;
    }
  }

  /**
   * Get ACL for a specific object
   * @param {number|string} spaceId - Space ID
   * @param {string} key - Object key
   * @returns {Promise<object>}
   */
  async getObjectAcl(spaceId, key) {
    try {
      const encodedKey = encodeURIComponent(key);
      const response = await api.get(
        `/spaces/${spaceId}/files/${encodedKey}/acl`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting object ACL:", error);
      throw error;
    }
  }

  // Access Keys Management

  /**
   * List access keys for a Space
   * @param {number|string} spaceId - Space ID
   * @returns {Promise<Array>}
   */
  async listAccessKeys(spaceId) {
    try {
      const response = await api.get(`/spaces/${spaceId}/access-keys`);
      return response.data || [];
    } catch (error) {
      console.error("Error listing access keys:", error);
      throw error;
    }
  }

  /**
   * Create access key for a Space
   * @param {number|string} spaceId - Space ID
   * @param {string} name - Key name
   * @param {string} accessLevel - Access level (read-only, read-write-delete)
   * @returns {Promise<object>}
   */
  async createAccessKey(spaceId, name, accessLevel = "read-only") {
    try {
      const response = await api.post(`/spaces/${spaceId}/access-keys`, {
        name,
        accessLevel,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating access key:", error);
      throw error;
    }
  }

  /**
   * Rename access key
   * @param {number|string} spaceId - Space ID
   * @param {number} keyId - Access key ID
   * @param {string} name - New name
   * @returns {Promise<object>}
   */
  async renameAccessKey(spaceId, keyId, name) {
    try {
      const response = await api.put(
        `/spaces/${spaceId}/access-keys/${keyId}`,
        {
          name,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error renaming access key:", error);
      throw error;
    }
  }

  /**
   * Delete access key
   * @param {number|string} spaceId - Space ID
   * @param {number} keyId - Access key ID
   * @returns {Promise<void>}
   */
  async deleteAccessKey(spaceId, keyId) {
    try {
      await api.delete(`/spaces/${spaceId}/access-keys/${keyId}`);
    } catch (error) {
      console.error("Error deleting access key:", error);
      throw error;
    }
  }
}

export default new SpacesService();
